import React from 'react';
import PropTypes from 'prop-types';
import { Form, Row, Col } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';

import {
  renderField,
  ACCURACY_SHOULD_SHOW_GATED,
  ACCURACY_FIELDS_BEFORE_STATE_ZIP,
  ACCURACY_FIELDS_BEFORE_AI,
  ACCURACY_FIELDS_AFTER_AI,
} from './field_renderer';
import { SALE_OR_LEASE } from './helpers';

export function ActionRequiredSection({ ctx }) {
  const { actionRequiredFields } = ctx;
  if (actionRequiredFields.size === 0) return null;
  return (
    <div className="form-vertical-field-container">
      <div>
        <div className="modal-title h6" style={{ fontSize: '1rem' }}>Action Required</div>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Resolve the highlighted fields to continue
        </p>
      </div>
      {[...actionRequiredFields].map(name => renderField(name, ctx))}
    </div>
  );
}

ActionRequiredSection.propTypes = {
  ctx: PropTypes.object.isRequired,
};

export function AiContentToggles({ ctx }) {
  const { formState, handleChange } = ctx;
  /* eslint-disable camelcase */
  const {
    sale_or_lease,
    generate_sale_description, generate_sale_bullets,
    generate_lease_description, generate_lease_bullets,
  } = formState;
  if (!sale_or_lease) return null;

  return (
    <Form.Group className="form-group">
      <Form.Label className="d-flex align-items-center gap-2">
        <FontAwesomeIcon icon={faSparkles} style={{ color: '#924FDC' }} />
        AI Content Generation
      </Form.Label>
      {sale_or_lease === SALE_OR_LEASE.SALE && (
        <>
          <Form.Check
            checked={generate_sale_description === true}
            id="review-generate-sale-description"
            label="Auto-generate sale description"
            type="switch"
            onChange={e => handleChange({ generate_sale_description: e.target.checked })}
          />
          <Form.Check
            checked={generate_sale_bullets === true}
            id="review-generate-sale-bullets"
            label="Auto-generate sale highlights"
            type="switch"
            onChange={e => handleChange({ generate_sale_bullets: e.target.checked })}
          />
        </>
      )}
      {sale_or_lease === SALE_OR_LEASE.LEASE && (
        <>
          <Form.Check
            checked={generate_lease_description === true}
            id="review-generate-lease-description"
            label="Auto-generate lease description"
            type="switch"
            onChange={e => handleChange({ generate_lease_description: e.target.checked })}
          />
          <Form.Check
            checked={generate_lease_bullets === true}
            id="review-generate-lease-bullets"
            label="Auto-generate lease highlights"
            type="switch"
            onChange={e => handleChange({ generate_lease_bullets: e.target.checked })}
          />
        </>
      )}
    </Form.Group>
  );
  /* eslint-enable camelcase */
}

AiContentToggles.propTypes = {
  ctx: PropTypes.object.isRequired,
};

export function AccuracySection({ ctx }) {
  const { formState, actionRequiredFields, populatedFields } = ctx;
  const { country, sale_or_lease } = formState; // eslint-disable-line camelcase
  const skip = name => actionRequiredFields.has(name);
  const shouldShow = name => populatedFields.has(name);

  const renderAccuracyField = (name) => {
    if (skip(name)) return null;
    if (ACCURACY_SHOULD_SHOW_GATED.has(name) && !shouldShow(name)) return null;
    if (name === 'sale_price' && sale_or_lease !== SALE_OR_LEASE.SALE) return null; // eslint-disable-line camelcase
    return renderField(name, ctx);
  };

  const showStateZipRow =
    (!skip('state') && country?.countryStates?.length > 0) ||
    (!skip('zip') && country?.zipLabel);

  return (
    <div className="form-vertical-field-container">
      {actionRequiredFields.size > 0 && (
        <div>
          <div className="modal-title h6" style={{ fontSize: '1rem' }}>Review for Accuracy</div>
          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
            Double-check these details before continuing
          </p>
        </div>
      )}

      {ACCURACY_FIELDS_BEFORE_STATE_ZIP.map(renderAccuracyField)}

      {showStateZipRow && (
        <Form.Group className="form-group">
          <Row>
            {!skip('state') && country?.countryStates?.length > 0 && (
              <Col md={8}>{renderField('state', ctx)}</Col>
            )}
            {!skip('zip') && country?.zipLabel && (
              <Col>{renderField('zip', ctx)}</Col>
            )}
          </Row>
        </Form.Group>
      )}

      {ACCURACY_FIELDS_BEFORE_AI.map(renderAccuracyField)}
      <AiContentToggles ctx={ctx} />
      {ACCURACY_FIELDS_AFTER_AI.map(renderAccuracyField)}
    </div>
  );
}

AccuracySection.propTypes = {
  ctx: PropTypes.object.isRequired,
};
